import React from 'react';
import useLogPage from '../useLogPage';
import LogFilters from '../LogFilters';
import LogTable from '../LogTable';
import LogStatCards from '../LogStatCards';
import { TrendChart, DonutChart } from '../LogCharts';
import { LogService } from '../../../services/activityLogService';

const STATUS_OPTIONS = [
  { value: 'created',    label: 'Created'    },
  { value: 'picked_up',  label: 'Picked Up'  },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'delivered',  label: 'Delivered'  },
  { value: 'returned',   label: 'Returned'   },
  { value: 'failed',     label: 'Failed'     },
];

const statusBadge = (v) => {
  const map = {
    created:    ['#DBEAFE','#1E40AF'],
    picked_up:  ['#FEF3C7','#92400E'],
    in_transit: ['#E0F2FE','#0369A1'],
    delivered:  ['#D1FAE5','#065F46'],
    returned:   ['#FEF3C7','#92400E'],
    failed:     ['#FEE2E2','#991B1B'],
  };
  const [bg, color] = map[v?.toLowerCase()] || ['#F3F4F6','#374151'];
  return <span className="al-badge" style={{ background: bg, color }}>{v || '—'}</span>;
};

const COLUMNS = [
  { key: 'id',           label: '#',           render: (v) => <span className="al-mono">{v}</span> },
  { key: 'shipment_id',  label: 'Shipment ID', render: (v) => <span className="al-mono">{v || '—'}</span> },
  { key: 'order_id',     label: 'Order ID',    render: (v) => <span className="al-mono">{v || '—'}</span> },
  { key: 'partner_name', label: 'Partner' },
  { key: 'status',       label: 'Status',      render: statusBadge },
  { key: 'location',     label: 'Location' },
  { key: 'event',        label: 'Event',       render: (v) => <span className="al-truncate" title={v}>{v || '—'}</span> },
  { key: 'created_at',   label: 'Time',        render: (v) => v ? new Date(v).toLocaleString('en-IN') : '—' },
];

const ShipmentLog = () => {
  const lp = useLogPage(LogService.shipments);
  const s = lp.stats || {};

  const handleExport = () => {
    const { search, status, dateFrom, dateTo } = lp.filters;
    LogService.exportShipments({
      ...(search   ? { search }              : {}),
      ...(status   ? { status }              : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo   ? { date_to:   dateTo   } : {}),
      format: 'excel',
    });
  };

  const stats = [
    { label: 'Total Events', value: lp.totalItems.toLocaleString(),          color: '#14B8A6' },
    { label: 'Delivered',    value: (s.delivered  ?? 0).toLocaleString(),    color: '#10B981' },
    { label: 'In Transit',   value: (s.in_transit ?? 0).toLocaleString(),    color: '#3B82F6' },
    { label: 'Failed',       value: (s.failed     ?? 0).toLocaleString(),    color: '#EF4444' },
  ];

  const donutData = lp.donut.length
    ? lp.donut
    : Object.entries(s.breakdown || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="al-page">
      <LogStatCards stats={stats} />

      <div className="al-charts">
        <TrendChart title="Shipment Events Over Time" data={lp.trend} color="#14B8A6" />
        <DonutChart  title="Shipment Status"          data={donutData} />
      </div>

      <LogFilters
        filters={lp.filters}
        onFilterChange={lp.handleFilterChange}
        onApply={lp.handleApply}
        onReset={lp.handleReset}
        statusOptions={STATUS_OPTIONS}
        loading={lp.loading}
      />

      <LogTable
        title="Shipment Logs"
        columns={COLUMNS}
        rows={lp.rows}
        loading={lp.loading}
        error={lp.error}
        page={lp.page}
        totalPages={lp.totalPages}
        totalItems={lp.totalItems}
        pageSize={lp.filters.pageSize}
        onPageChange={lp.handlePageChange}
        onExport={handleExport}
        onRefresh={lp.handleRefresh}
      />
    </div>
  );
};

export default ShipmentLog;
